import { useTranslation } from "react-i18next";
import { useState } from "react";
import { ChevronLeft, X } from "lucide-react";
import type { FacilityPermissionLevel } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { Drawer, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { CategoryPicker } from "./CategoryPicker.js";
import { FacilityOverview } from "./FacilityOverview.js";
import { ManualBinDialog, type ManualBinKind } from "./ManualBinDialog.js";

export type FacilityDetailsMode = "overview" | "manage";

export interface FacilityDetailsDrawerProps {
  facilityId: number;
  facilityName: string;
  /** Direct subfacility count for the header count line. */
  childCount: number;
  /** Categories attached to this facility (live via the tree refetch). */
  categoryCount: number;
  /** The caller's effective permission on this facility (drives the Overview access label). */
  permission: FacilityPermissionLevel | null;
  /** Facility-management capability — `bypass || permission ∈ {manage, access_manage}`, computed by the CALLER. Must
   *  NOT be the manuals-scoped `canManageManuals` (true for `manuals_manage`/`confirm`), which would let a manuals-only
   *  caller reach the mutating category picker (ADR-016 panel #1). Gates Manage mode + the "Manage area" affordance. */
  canManage: boolean;
  session: AuthSession | null;
  /** Which mode to open in — the facility kebab opens "manage" (legacy behaviour); the Area-info icon opens "overview". */
  initialMode?: FacilityDetailsMode;
  /** The manuals capability on this facility, distinct from `canManage`: gates the trash and the actions in both lists. */
  canManageManuals: boolean;
  onClose: () => void;
}

/**
 * The canonical per-facility slide-over (ADR-016 §C) — unifies the old "Manage facility" drawer with the new area
 * "Info" into ONE drawer with two internal modes:
 * - **Overview** — the area at a glance for ANY viewer ({@link FacilityOverview}: manual counts, effective access, copy
 *   link) + a gated "Manage area" switch, and the archived manuals + the trash ({@link ManualBinDialog}, ADR-056).
 * - **Manage** — the category picker (attach/detach/create), for facility MANAGERS only.
 *
 * The drawer is the authority for its own body: it never renders Manage without `canManage`, even if a caller passes
 * `initialMode="manage"` — so a view-only caller always lands on Overview (defence in depth over the trigger's gate).
 * Modal (the shared {@link Drawer} primitive): focus-trapped, Esc/backdrop dismiss, restores focus to the trigger.
 */
export function FacilityDetailsDrawer({
  facilityId,
  facilityName,
  childCount,
  categoryCount,
  permission,
  canManage,
  session,
  initialMode = "overview",
  canManageManuals,
  onClose,
}: FacilityDetailsDrawerProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<FacilityDetailsMode>(initialMode === "manage" && canManage ? "manage" : "overview");
  const showManage = mode === "manage" && canManage;
  const [bin, setBin] = useState<ManualBinKind | null>(null);

  return (
    <Drawer isOpen isDismissable onOpenChange={(open) => !open && onClose()}>
      {/* Facility identity FIRST (eyebrow · name · counts), divided from the mode body below. */}
      <DrawerHeader>
        <div className="min-w-0">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{t("facilityManagement.categories.areaEyebrow")}</p>
          <DrawerTitle className="truncate">{facilityName}</DrawerTitle>
          <p className="text-muted-foreground mt-0.5 text-sm">
            {t("facilityManagement.categories.subfacilityCount", { count: childCount })} ·{" "}
            {t("facilityManagement.categories.categoryCount", { count: categoryCount })}
          </p>
        </div>
        <Button variant="ghost" size="icon-sm" aria-label={t("facilityManagement.actions.close")} onPress={onClose} className="-mt-1 -mr-2 shrink-0">
          <X className="size-4" />
        </Button>
      </DrawerHeader>

      {/* The body scrolls (Overview + the category picker can exceed the viewport); the header stays put. */}
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
        {showManage ? (
          <>
            <Button variant="outline" size="sm" onPress={() => setMode("overview")} className="dark:border-border self-start">
              <ChevronLeft aria-hidden="true" />
              {t("dashboard.overview.raw")}
            </Button>
            <div className="flex flex-col gap-1">
              <h3 className="text-lg font-semibold">{t("facilityManagement.categories.heading")}</h3>
              <DrawerDescription>
                {t("facilityManagement.categories.help")}
              </DrawerDescription>
            </div>
            <CategoryPicker facilityId={facilityId} session={session} />
          </>
        ) : (
          <FacilityOverview
            facilityId={facilityId}
            permission={permission}
            canManage={canManage}
            session={session}
            onManage={() => setMode("manage")}
            canManageManuals={canManageManuals}
            onOpenBin={setBin}
          />
        )}
      </div>
      {bin ? (
        <ManualBinDialog
          kind={bin}
          facilityId={facilityId}
          facilityName={facilityName}
          canManage={canManageManuals}
          session={session}
          onClose={() => setBin(null)}
        />
      ) : null}
    </Drawer>
  );
}
